import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FaMinus } from 'react-icons/fa6';
import { Spinner } from 'reactstrap';
import CountrySelectService from '../../components/CSC/CountrySelect/CountrySelectService';
import ButtonGroupNew from '../../pages/Form/forms/BusinessLiteForms/components/ButtonGroupNew';
import { DialogContext } from '../../store/context/DialogContext';
import { useTranslation } from '../../store/context/TranslationContext';
import {
    PayNowFooter,
    STATUS,
    formatCurrency,
    formatCurrencyAsZero,
    getCart,
    paymentCopy
} from './CartService';
import DepositAmountField from './DepositAmountField';
import { observer } from 'mobx-react-lite';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../InputField/testId';


const IPlusCart = ({
    data,
    applicationId,
    cartId,
    onPayNow,
    paymentStatus,
    onBackClick,
    totalPaidAmount,
    handleUpdateFormData,
    applicationFormData,
    testId,
}) => {
    const { COUNTRY_MASTER_KEYS, cyclosCountryMasterMap } = CountrySelectService;
    const { t } = useTranslation();
    const { showError } = useContext(DialogContext);
    const [errors, setErrors] = useState({})
    const [cart, setCart] = useState({});
    const [loading, setLoading] = useState(false);
    const resolvedTestId = resolveAutomationTestId(testId);

    const {
        I_PLUS_APPLICATION_FEE: appFeeKey,
        I_PLUS_CARD_FEE: cardFeeKey,
        I_PLUS_ACCOUNT_OPENING_FEE: accountOpeningFeeKey,
        VISA_CARD: visaKey,
        COUNTRY: countryKey,
        OPTIONAL_MAXIMUM_DEPOSIT_VIA_STRIPE: maxKey,
        OPTIONAL_MINIMUM_INITIAL_DEPOSIT_VIA_STRIPE: minKey
    } = COUNTRY_MASTER_KEYS;

    const country = data?.address?.country;

    const fees = useMemo(() => ({
        visaAvailable: applicationFormData?.extension?.countryMasterMap?.[country]?.[visaKey] === 'Yes',
        minInitialDepositAmount: applicationFormData?.extension?.countryMasterMap?.[country]?.[minKey],
        maxInitialDepositAmount: applicationFormData?.extension?.countryMasterMap?.[country]?.[maxKey],
    }), [cart, country, appFeeKey, cardFeeKey, visaKey, JSON.stringify(applicationFormData?.extension?.countryMasterMap), minKey, maxKey]);

    const cartItems = useMemo(() => ({
        accountOpeningFee: cart?.cartItems?.find(item => item.name == accountOpeningFeeKey),
        cardFee: cart?.cartItems?.find(item => item.name == cardFeeKey),
        applicationFee: cart?.cartItems?.find(item => item?.name == appFeeKey),
        depositAmount: cart?.cartItems?.find(item => item?.name == "Deposit Amount"),
    }), [cart, appFeeKey]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const fresh = await getCart(applicationId, cartId);
            setCart(fresh);
        } catch (err) {
            showError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (cartId) fetchData();
    }, [applicationId, cartId]);


    const handleDepositAmountChange = async (initialDepositAmount) => {
        setLoading(true);
        try {
            const response = await handleUpdateFormData({
                ...data,
                initialDepositAmount
            });
            if (response?.status == 200) {
                await fetchData();
            }
        } catch (err) {
            showError(err);
        } finally {
            setLoading(false);
        }
    }

    const handleNextClick = () => {
        try {
            // to check if error is there? from errors state
            if (errors?.initialDepositAmount) {
                showError(errors?.initialDepositAmount);
                return;
            }
            onPayNow(cart?.payable == 0)
        } catch (error) {

        }
    }

    return (
        <div className={`d-flex flex-column justify-content-center align-items-center`} data-testid={resolvedTestId} data-record-id={cartId}>
            {loading && (
                <div className="fv_amount_due_full_screen_loader" data-testid={buildAutomationChildTestId(resolvedTestId, "loading")}>
                    <Spinner color="primary" />
                </div>
            )}

            <div className='fv_amount_due' data-testid={buildAutomationChildTestId(resolvedTestId, "amount-card")}>
                <div className='fv_amount_due_header' data-testid={buildAutomationChildTestId(resolvedTestId, "header")}>
                    {t('Application Fees')} ({cyclosCountryMasterMap?.[country]?.[countryKey]})
                </div>

                {cartItems.accountOpeningFee && <div className="fv_amount_due_row" data-testid={buildAutomationChildTestId(resolvedTestId, "account-opening-fee")}>
                    <span>{t('Account Opening Fee')}</span>
                    <span>{formatCurrency((cartItems.accountOpeningFee?.price / 100).toFixed(2))}</span>
                </div>}

                {cartItems.applicationFee && <div className="fv_amount_due_row" data-testid={buildAutomationChildTestId(resolvedTestId, "application-fee")}>
                    <span>{t('Application Fee')}</span>
                    <span>{formatCurrencyAsZero((cartItems.applicationFee?.price / 100).toFixed(2))}</span>
                </div>}

                {cartItems.cardFee && <div className={`fv_amount_due_row`} data-testid={buildAutomationChildTestId(resolvedTestId, "card-fee")}>
                    <span className='d-flex gap-2 align-items-center'> {t("Visa Debit Card")} </span>
                    <span>{formatCurrency((cartItems.cardFee?.price / 100).toFixed(2))}</span>
                </div>}

                <div className="fv_amount_due_total " data-testid={buildAutomationChildTestId(resolvedTestId, "initial-deposit")}>
                    <span>{t('Initial Deposit Amount')}</span>
                    <DepositAmountField
                        testId={buildAutomationChildTestId(resolvedTestId, "initial-deposit-field")}
                        minInitialDepositAmount={fees?.minInitialDepositAmount}
                        maxInitialDepositAmount={fees?.maxInitialDepositAmount}
                        value={cartItems?.depositAmount?.price}
                        handleDepositAmountChange={handleDepositAmountChange}
                        loading={loading || paymentStatus === STATUS?.PROCESSING}
                        onError={(errorMsg) => setErrors((prevErrors) => ({ ...prevErrors, initialDepositAmount: errorMsg }))}
                    />
                </div>

                {cart?.payable != cart?.total && <div>
                    <div className='fv_amount_due_total' data-testid={buildAutomationChildTestId(resolvedTestId, "total-due")}>
                        <span>{t("Total Due")}</span>
                        <span>{formatCurrencyAsZero((cart?.total / 100).toFixed(2))}</span>
                    </div>

                    {totalPaidAmount && <div className=' fv_amount_due_row mt-2 success ' data-testid={buildAutomationChildTestId(resolvedTestId, "amount-paid")}>
                        <span>{"Amount Paid"}</span>
                        <span className='d-flex align-items-center gap-2 '> {formatCurrencyAsZero(((totalPaidAmount) / 100).toFixed(2))}</span>
                    </div>}
                </div>}

                <div className='fv_amount_due_total due ' data-testid={buildAutomationChildTestId(resolvedTestId, "balance-due")}>
                    <span>{cart?.payable != cart?.total ? t("Balance Due") : t("Total Due")}</span>
                    <span>{formatCurrencyAsZero((cart?.payable / 100).toFixed(2))}</span>
                </div>

                <div data-testid={buildAutomationChildTestId(resolvedTestId, "footer-copy")}>
                    <PayNowFooter />
                </div>
            </div>
            <div className='w-100' data-testid={buildAutomationChildTestId(resolvedTestId, "footer")}>
                <ButtonGroupNew
                    testId={buildAutomationChildTestId(resolvedTestId, "navigation")}
                    primaryActionTestIdSuffix={cart?.payable == 0 ? "submit-application" : "pay-now"}
                    onNextClick={handleNextClick}
                    onBackClick={() => { onBackClick() }}
                    step={4}
                    isDisabled={paymentStatus === STATUS?.PROCESSING || loading}
                    nextText={cart?.payable == 0 ? t(paymentCopy?.submit_application) : t(paymentCopy?.pay_now)}
                    asLabel={false}
                    loading={paymentStatus === STATUS?.PROCESSING}
                />
            </div>

        </div>
    );
};

export default observer(IPlusCart);
